import {
  createContext,
  useContext,
  useRef,
  useCallback,
  ReactNode,
  useState
} from 'react'

import { useKeyPressWithConfigs } from 'hooks'
import { useSelector } from 'react-redux'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { calculatePercentOf, getPercentageFrom, parseFloatAndFix } from 'utils'
import { editorSelectors } from 'store/features/editor/selectors'
import { IMediaItem } from 'types/media'
import { EVENT_TYPES, eventBus } from 'services/event-bus'

export interface IDropState {
  isDragOver: boolean
  rowZIndex: number | null
  positionPx: number | null
  widthPx: number | null
  // -1 means the gap above the first row. null when not over a gap.
  targetGapAfterLineIndex: number | null
}

export const initialDropState: IDropState = {
  isDragOver: false,
  rowZIndex: null,
  positionPx: null,
  widthPx: null,
  targetGapAfterLineIndex: null
}

interface ITimelineContextType {
  isPlaying: boolean
  playHeadRef: React.RefObject<HTMLDivElement | null>
  playHeadClickableAreaRef: React.RefObject<HTMLDivElement | null>
  scrollableContainerRef: React.RefObject<HTMLDivElement | null>
  onPlay: () => void
  onPause: () => number
  setPlayHeadCurrentTime: (time: number, skip?: boolean) => void
  getCurrentTimeImmediate: () => number
  getCurrentTimeByOffsetLeft: (offsetLeft: number) => number
  setPlayHeadOffsetLeft: (offsetLeft: number) => void
  getPlayHeadOffsetLeft: () => number
  magnetizingLineRef: React.RefObject<HTMLDivElement | null>
  isReactSelectoMultiDragging: boolean
  setIsReactSelectoMultiDragging: (isReactSelectoMultiDragging: boolean) => void
  isMultiDragging: boolean
  setIsMultiDragging: (isMultiDragging: boolean) => void
  resumePlaying: () => void
  previewMediaItems: IMediaItem[] | null
  setPreviewMediaItems: (items: IMediaItem[] | null) => void
  dropState: IDropState
  setDropState: React.Dispatch<React.SetStateAction<IDropState>>
}

const TimelineContext = createContext<ITimelineContextType | null>(null)

export const TimelineContextProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const playHeadRef = useRef<HTMLDivElement>(null)
  const playHeadClickableAreaRef = useRef<HTMLDivElement>(null)
  const projectDuration = useSelector(projectDataSelectors.duration)
  const [isReactSelectoMultiDragging, setIsReactSelectoMultiDragging] =
    useState(false)
  const [isMultiDragging, setIsMultiDragging] = useState(false)
  const [previewMediaItems, setPreviewMediaItems] = useState<
    IMediaItem[] | null
  >(null)
  const [dropState, setDropState] = useState<IDropState>(initialDropState)

  const [isPlaying, setIsPlaying] = useState(false)
  const magnetizingLineRef = useRef<HTMLDivElement>(null)
  const isAnyBuffering = useSelector(editorSelectors.isAnyBuffering)

  const getPlayHeadOffsetLeft = useCallback(() => {
    if (!playHeadRef.current || !playHeadClickableAreaRef.current) return 0
    const playHeadAbsoluteLeft =
      playHeadRef.current.getBoundingClientRect().left
    const playHeadAreaLeft =
      playHeadClickableAreaRef.current.getBoundingClientRect().left
    return playHeadAbsoluteLeft - playHeadAreaLeft
  }, [playHeadRef, playHeadClickableAreaRef])

  const getCurrentTimeByOffsetLeft = useCallback(
    (offsetLeft: number) => {
      if (!playHeadClickableAreaRef.current) return 0

      const playableAreaWidth =
        playHeadClickableAreaRef.current.getBoundingClientRect().width
      const leftInPercents = getPercentageFrom(offsetLeft, playableAreaWidth)

      const currentTime = calculatePercentOf(leftInPercents, projectDuration)
      return parseFloatAndFix(currentTime)
    },
    [projectDuration]
  )

  const setPlayHeadOffsetLeft = useCallback(
    (offsetLeft: number) => {
      if (!playHeadRef.current || !playHeadClickableAreaRef.current) return
      const playHeadAreaWidth =
        playHeadClickableAreaRef.current.getBoundingClientRect().width
      const leftInPercents = getPercentageFrom(offsetLeft, playHeadAreaWidth)

      playHeadRef.current.style.left = `${leftInPercents}%`
    },
    [playHeadRef, playHeadClickableAreaRef]
  )

  const stopPlayHeadAnimation = useCallback(() => {
    if (!playHeadRef.current) return
    playHeadRef.current.style.animationName = ''
  }, [playHeadRef])

  const getCurrentTimeImmediate = useCallback(() => {
    const offsetLeft = getPlayHeadOffsetLeft()
    return getCurrentTimeByOffsetLeft(offsetLeft)
  }, [getCurrentTimeByOffsetLeft, getPlayHeadOffsetLeft])

  const setPlayHeadCurrentTime = useCallback(
    (time: number, skip: boolean = false) => {
      if (skip) {
        return
      }
      stopPlayHeadAnimation()
      const offsetLeftByPercent = getPercentageFrom(time, projectDuration)
      const playHeadAreaWidth =
        playHeadClickableAreaRef.current?.getBoundingClientRect().width || 0
      const offsetLeft = calculatePercentOf(
        offsetLeftByPercent,
        playHeadAreaWidth
      )
      setPlayHeadOffsetLeft(offsetLeft)
      eventBus.publish(EVENT_TYPES.PLAYBACK_TIME_CHANGE, time)
    },
    [projectDuration, setPlayHeadOffsetLeft, stopPlayHeadAnimation]
  )

  const startPlayHeadAnimation = useCallback(() => {
    if (!playHeadRef.current || !playHeadClickableAreaRef.current) return
    const playbackCurrentTime = getCurrentTimeImmediate()
    const animationDuration = projectDuration - playbackCurrentTime
    const playableAreaWidth =
      playHeadClickableAreaRef.current.getBoundingClientRect().width
    const offsetLeft =
      playHeadRef.current.getBoundingClientRect().left -
      playHeadClickableAreaRef.current.getBoundingClientRect().left
    const leftInPercents = getPercentageFrom(offsetLeft, playableAreaWidth)

    playHeadRef.current.style.setProperty(
      '--playhead-start',
      `${leftInPercents}%`
    )
    playHeadRef.current.style.animationName = 'playhead-animation'
    playHeadRef.current.style.animationDuration = `${animationDuration}s`
  }, [
    playHeadRef,
    playHeadClickableAreaRef,
    projectDuration,
    getCurrentTimeImmediate
  ])

  const onPause = useCallback(() => {
    const playHeadOffsetLeft = getPlayHeadOffsetLeft()
    if (isPlaying) {
      setPlayHeadOffsetLeft(playHeadOffsetLeft)
      stopPlayHeadAnimation()
      setIsPlaying(false)
    }

    return playHeadOffsetLeft
  }, [
    getPlayHeadOffsetLeft,
    setPlayHeadOffsetLeft,
    stopPlayHeadAnimation,
    isPlaying
  ])

  const onPlay = useCallback(() => {
    startPlayHeadAnimation()
    setIsPlaying(true)
  }, [startPlayHeadAnimation])

  const resumePlaying = useCallback(() => {
    // if timeline was playing before certain action, resume it
    if (isPlaying) {
      onPlay()
    }
  }, [onPlay, isPlaying])

  const togglePlay = useCallback(() => {
    if (isAnyBuffering) return

    if (isPlaying) {
      onPause()
    } else {
      onPlay()
    }
  }, [isPlaying, onPause, onPlay, isAnyBuffering])

  useKeyPressWithConfigs('space', togglePlay)

  return (
    <TimelineContext.Provider
      value={{
        isPlaying,
        playHeadRef,
        playHeadClickableAreaRef,
        scrollableContainerRef,
        onPlay,
        onPause,
        resumePlaying,
        getPlayHeadOffsetLeft,
        setPlayHeadOffsetLeft,
        setPlayHeadCurrentTime,
        getCurrentTimeByOffsetLeft,
        getCurrentTimeImmediate,
        magnetizingLineRef,
        isReactSelectoMultiDragging,
        setIsReactSelectoMultiDragging,
        isMultiDragging,
        setIsMultiDragging,
        previewMediaItems,
        setPreviewMediaItems,
        dropState,
        setDropState
      }}
    >
      {children}
    </TimelineContext.Provider>
  )
}

export const useTimelineContext = () => {
  const context = useContext(TimelineContext)
  if (!context) {
    throw new Error('useTimelineContext must be used within TimelineProvider')
  }
  return context
}
